import { IUser } from '@/modules/Auth/auth-types'
import { emailValidator } from '@/utils/validations'
import { IFirebaseAuthentication } from '@/modules/Firebase/firebase-types'
import { capitalize } from '@/utils/fnUtils'

export interface IAddress {
  cep?: string
  streetName?: string
  number?: string
  complement?: string
  state?: string
  city?: string
  neighborhood?: string
  country?: string
}

export class User {
  private readonly _name: string

  constructor(
    private _id: number,
    private _cpf: string,
    private _patientId: number,
    _name: string,
    public photo: string | null,
    private _token: string,
    private _refreshToken: string | null,
    private _clinic: { id: number; name: string },
    public birthdate: string | null,
    public sex: string | null,
    public cellphone: string | null,
    public email: string | null,
    public login: string,
    public ip: string | null,
    public address: IAddress,
    public firebaseToken: string,
    private _firebaseAuthentication: IFirebaseAuthentication,
    private _isAllowedToNewApplication: boolean,
    private _isAcceptedBenefitTerm: boolean,
    public passportExpirationDate: string | null,
    public dateIssuePassport: string | null,
    public passport: string | null,
    public idCountry: string | null,
    public healthInsuranceNumber: string | null,
    public issuingBodyRg: string | null,
    public dateIssueRg: string | null,
    public rg: string | null,
    public ethnicity: string | null,
    public idGenre: string | null,
    public idNacionality: string | null,
    public idRaceOrColor: string | null,
    public nameMother: string | null,
    public unknownMother: boolean | null,
    public socialName: string | null,
    public cns: string | null,
    public birthCity: string | null,
    public idUfBirth: number | null,
    public isHealthTeam: boolean, // public allowLogin: boolean, // public directAccessLink?: string, // public androidAppLink?: string, // public iosAppLink?: string,
    public holder: string | null,
    public needToUpdateAccountData: boolean,
    public flagAccessedBefore: boolean | null,
  ) {
    this._name = capitalize(_name?.toLowerCase())
  }

  get id(): number {
    return this._id
  }

  get cpf(): string {
    return this._cpf
  }

  get patientId(): number {
    return this._patientId
  }

  get token(): string {
    return this._token
  }

  get refreshToken(): string {
    return this._refreshToken
  }

  get name(): string {
    return this._name
  }

  get socialNameOrName(): string {
    return this.socialName ? this.socialName : this.name
  }

  get shortName(): string {
    if (this.socialName) {
      const arrSocialName = this.socialName.split(' ')
      if (arrSocialName.length === 1) return arrSocialName[0]
      if (arrSocialName[1].length > 3)
        return capitalize(arrSocialName.slice(0, 2).join(' '))
      else
        return capitalize(
          arrSocialName[0] + ' ' + arrSocialName[arrSocialName.length - 1],
        )
    } else {
      const arrName = this.name.split(' ')
      if (arrName.length === 1) return arrName[0]
      if (arrName[1].length > 3) return arrName.slice(0, 2).join(' ')
      else return arrName[0] + ' ' + arrName[arrName.length - 1]
    }
  }

  get clinic(): { id: number; name: string } {
    return this._clinic
  }

  get haveDataPendence(): boolean {
    return (
      !emailValidator(this.email) ||
      !['F', 'M', 'OUTRO'].includes(this.sex) ||
      this.birthdate === null ||
      !this.cellphone ||
      !this.birthdate ||
      this.birthdate.length !== 10
    )
  }

  get firebaseAuthentication(): IFirebaseAuthentication {
    return this._firebaseAuthentication
  }

  get isAllowedToNewApplication(): boolean {
    return this._isAllowedToNewApplication
  }

  get isAcceptedBenefitTerm(): boolean {
    return this._isAcceptedBenefitTerm
  }

  public buildUserFromClass(): IUser {
    return {
      // permiteLogin: this.allowLogin,
      // linkAcessoDireto: this.directAccessLink,
      // linkAplicativoAndroid: this.androidAppLink,
      // linkAplicativoIos: this.iosAppLink,
      token: this.token,
      ip: this.ip,
      tokenFirebase: this.firebaseToken,
      firebaseAuthentication: this._firebaseAuthentication,
      usuario: {
        id: this.id,
        nome: this.name,
        login: this.login,
        urlFoto: this.photo,
        clinica: {
          id: this.clinic.id,
          nome: this.clinic.name,
        },
        flagAcessou: this.flagAccessedBefore,
        paciente: {
          id: this.patientId,
          cpf: this.cpf,
          dataNascimento: this.birthdate,
          dataValidadePassaporte: this.passportExpirationDate,
          dataEmissaoPassaporte: this.dateIssuePassport,
          idPaisNascimentoEstrangeiro: this.idCountry,
          orgaoEmissorIdentidade: this.issuingBodyRg,
          dataEmissaoIdentidade: this.dateIssueRg,
          identidade: this.rg,
          etnia: this.ethnicity,
          idGenero: this.idGenre,
          idNacionalidade: this.idNacionality,
          idRacaCor: this.idRaceOrColor,
          nomeMae: this.nameMother,
          flagMaeDesconhecida: this.unknownMother,
          nomeSocial: this.socialName,
          cns: this.cns,
          municipioNascimento: this.birthCity,
          idUfNascimento: this.idUfBirth,
          passaporte: this.passport,
          telefoneCelular: this.cellphone,
          sexo: this.sex,
          email: this.email,
          cep: this.address.cep,
          bairro: this.address.neighborhood,
          cidade: this.address.city,
          complemento: this.address.complement,
          endereco: this.address.streetName,
          numeroLogradouro: this.address.number,
          carteiraConvenio: this.healthInsuranceNumber,
          pais: this.address.country,
          uf: this.address.state,
          flagAps: this.isHealthTeam,
          titularidade: this.holder,
        },
      },
      flagNovaExperiencia: this.isAllowedToNewApplication,
      flagExisteBeneficioAceito: this._isAcceptedBenefitTerm,
    }
  }
}

export const buildUserFromInterface = (user: IUser): User => {
  return new User(
    user.usuario.id,
    user.usuario.paciente.cpf,
    user.usuario.paciente.id,
    user.usuario.nome,
    user.usuario.urlFoto,
    user.token,
    user.refreshToken,
    { id: user.usuario.clinica.id, name: user.usuario.clinica.nome },
    user.usuario.paciente.dataNascimento,
    user.usuario.paciente.sexo,
    user.usuario.paciente.telefoneCelular,
    user.usuario.paciente.email,
    user.usuario.login,
    user.ip,
    {
      cep: user.usuario.paciente.cep,
      streetName: user.usuario.paciente.endereco,
      number: user.usuario.paciente.numeroLogradouro || '',
      complement: user.usuario.paciente.complemento,
      neighborhood: user.usuario.paciente.bairro,
      state: user.usuario.paciente.uf,
      city: user.usuario.paciente.cidade,
      country: user.usuario.paciente.pais,
    },
    user.tokenFirebase,
    user.firebaseAuthentication,
    user.flagNovaExperiencia,
    user.flagExisteBeneficioAceito,
    user.usuario.paciente.dataValidadePassaporte,
    user.usuario.paciente.dataEmissaoPassaporte,
    user.usuario.paciente.passaporte,
    user.usuario.paciente.idPaisNascimentoEstrangeiro,
    user.usuario.paciente.carteiraConvenio,
    user.usuario.paciente.orgaoEmissorIdentidade,
    user.usuario.paciente.dataEmissaoIdentidade,
    user.usuario.paciente.identidade,
    user.usuario.paciente.etnia,
    user.usuario.paciente.idGenero,
    user.usuario.paciente.idNacionalidade,
    user.usuario.paciente.idRacaCor,
    user.usuario.paciente.nomeMae,
    user.usuario.paciente.flagMaeDesconhecida,
    user.usuario.paciente.nomeSocial,
    user.usuario.paciente.cns,
    user.usuario.paciente.municipioNascimento,
    user.usuario.paciente.idUfNascimento,
    user.usuario.paciente.flagAps,
    user.usuario.paciente.titularidade,
    user.usuario.paciente.pendenteAtualizacaoDadosCadastrais,
    user.usuario.flagAcessou,
    // user.permiteLogin,
    // user.linkAcessoDireto,
    // user.linkAplicativoAndroid,
    // user.linkAplicativoIos,
  )
}
